import { useState } from 'react';
import type {
  Grain,
  PeriodKey,
  RecruiterOffering,
  Source,
  Specialty,
  Targeting,
} from '../api/types.js';
import { sanitizeEnumRecord, usePersistentState } from './persist.js';

/**
 * Chart filters and table filters as two **separate** stores (FR-034, PRD §6.2).
 *
 * They are separate because the requirement says so, and the requirement says so because they
 * answer different questions: the chart shows a trend under one lens, the table compares
 * regions under another. Sharing one store would make narrowing the chart to Nursing silently
 * re-scope every table row too — which reads as a bug even when it is consistent.
 *
 * Filter state lives in UI state and is not required to appear in the URL (FR-022). A store may
 * additionally be given a `storageKey`, which makes it survive unmount and reload for the rest of
 * the browser session — see `persist.ts` for why that is session-scoped and sanitised.
 */

export interface ChartFilters {
  source: Source;
  targeting: Targeting;
  specialty: Specialty;
  grain: Grain;
}

export interface TableFilters {
  source: Source;
  targeting: Targeting;
  specialty: Specialty;
  period: PeriodKey;
}

/**
 * The accepted value of every filter, in one place.
 *
 * These exist because filter state is now **restored from storage** (`persist.ts`), and a
 * restored value is untrusted: it may have been written by a deploy whose options differed. The
 * lists are what turns an unrecognised stored value back into a default instead of into a 422
 * from an endpoint that has never heard of it. They are the same closed sets the API validates
 * against, so a new option has to be added here as well as to the page's control.
 */
const SOURCE_VALUES: readonly Source[] = ['all', 'internal'];
const TARGETING_VALUES: readonly Targeting[] = ['all', 'target'];
const SPECIALTY_VALUES: readonly Specialty[] = ['all', 'nursing', 'allied'];
const GRAIN_VALUES: readonly Grain[] = ['daily', 'weekly', 'monthly', 'quarterly'];
const PERIOD_VALUES: readonly PeriodKey[] = ['MTD', '30d', '90d', '6M', '12M', '24M', 'YTD'];
const OFFERING_VALUES: readonly RecruiterOffering[] = ['all', 'nursing', 'allied'];

/** PRD §6.4 defaults. A fresh load must present exactly these (SC-010). */
export const CHART_DEFAULTS: ChartFilters = {
  source: 'all',
  targeting: 'all',
  specialty: 'all',
  grain: 'monthly',
};

export const TABLE_DEFAULTS: TableFilters = {
  source: 'all',
  targeting: 'all',
  specialty: 'all',
  period: '90d',
};

/** The Detail breakdown defaults differently — 30d / All (FR-038). */
export const CLIENT_DEFAULTS = {
  period: '30d' as PeriodKey,
  targeting: 'all' as Targeting,
};

export const DEFAULT_VIEW: 'table' | 'cards' = 'table';

/**
 * `storageKey` opts a page into remembering its chart filters for the rest of the browser session
 * (see `persist.ts`). Omitted, the store is plain component state and resets on unmount — which
 * is what the Regions surfaces still do, so this change touches only the page that asked for it.
 */
export function useChartFilters(initial: ChartFilters = CHART_DEFAULTS, storageKey?: string) {
  const [filters, setFilters] = usePersistentState<ChartFilters>(
    storageKey,
    initial,
    sanitizeEnumRecord(initial, {
      source: SOURCE_VALUES,
      targeting: TARGETING_VALUES,
      specialty: SPECIALTY_VALUES,
      grain: GRAIN_VALUES,
    }),
  );

  return {
    filters,
    set: <K extends keyof ChartFilters>(key: K, value: ChartFilters[K]) =>
      setFilters((current) => ({ ...current, [key]: value })),
    reset: () => setFilters(initial),
  };
}

export function useTableFilters(initial: TableFilters = TABLE_DEFAULTS) {
  const [filters, setFilters] = useState<TableFilters>(initial);

  return {
    filters,
    set: <K extends keyof TableFilters>(key: K, value: TableFilters[K]) =>
      setFilters((current) => ({ ...current, [key]: value })),
    reset: () => setFilters(initial),
  };
}

/**
 * The two periods that are a **partial span of a calendar unit**, counted from its start.
 *
 * `MTD` and `YTD` grow through the window they name — on the 2nd of a month MTD is two days of
 * activity — where every other option is a trailing window of a fixed length (PRD §8.4).
 */
const TO_DATE_PERIODS: readonly PeriodKey[] = ['MTD', 'YTD'];

/**
 * Whether a table showing `period` may draw trend arrows (owner decision, 2026-08-28, PRD §9.5).
 *
 * The arrow never *moves* with the period — it is a fixed 30-day trailing comparison from `asOf`
 * (FR-054, R-12), and that stays true. What changes is whether it is drawn at all: beside an MTD
 * or YTD figure the two describe spans of different lengths, and a ▲ next to two days of activity
 * reads as a claim about those two days rather than about the trailing month. The other periods
 * are trailing windows themselves, so the arrow is at least the same kind of measure as the
 * number it sits on.
 *
 * The 30- and 90-day figures stay in every tooltip and accessible description regardless — this
 * withholds the glyph, not the data (`<MetricCell showTrendArrow>`).
 */
export function showsTrendArrows(period: PeriodKey): boolean {
  return !TO_DATE_PERIODS.includes(period);
}

// ---------------------------------------------------------------------------
// Cards view sorting (blueprint § region cards)
// ---------------------------------------------------------------------------

/**
 * What the Cards view can be ordered by, in the blueprint's order.
 *
 * Six figures, not seventeen. The cards are a *scan* surface — the table is where every column
 * is available and every header sorts — so the list is the six a lead actually ranks regions by,
 * and the labels are the blueprint's own rather than the table's short codes.
 *
 * Each value is the metric key it reads out of `metricsByCombo`, so the ordering is over the
 * same numbers the cards are showing, under the same filters. `recruiters` is the one that does
 * not move with the period, which is a property of the column, not of the sort.
 */
export const CARD_SORT_OPTIONS = [
  { value: 'activeToa', label: 'TOA' },
  { value: 'bob', label: 'BoB' },
  { value: 'jobCount', label: 'Jobs' },
  { value: 'subs', label: 'Subs' },
  { value: 'offers', label: 'Offers' },
  { value: 'recruiters', label: 'Recruiters' },
] as const;

export type CardSortKey = (typeof CARD_SORT_OPTIONS)[number]['value'];

export interface CardSortState {
  key: CardSortKey;
  direction: 'asc' | 'desc';
}

/** The blueprint opens on TOA, descending — the biggest desks first. */
export const CARD_SORT_DEFAULT: CardSortState = { key: 'activeToa', direction: 'desc' };

/** The key into a row's precomputed `metricsByCombo` map. */
export function comboKey(filters: {
  source: Source;
  targeting: Targeting;
  specialty: Specialty;
}): string {
  return `${filters.source}|${filters.targeting}|${filters.specialty}`;
}

// ---------------------------------------------------------------------------
// Delivery (spec 002, PRD-Delivery §6.6)
// ---------------------------------------------------------------------------

/**
 * The Delivery table's own filters, separate from its chart's for the same reason Regions
 * separates them (FR-D13): the chart shows a trend under one lens, the table compares teams
 * under another, and one control must never silently change two things.
 *
 * `recruiterOffering` is the clearest case. It narrows a manager's **recruiter list** and is
 * deliberately not the chart's `specialty` — a different name for a different control, so the two
 * cannot be wired together by accident.
 */
export interface DeliveryTableFilters {
  source: Source;
  targeting: Targeting;
  specialty: Specialty;
  period: PeriodKey;
  recruiterOffering: RecruiterOffering;
}

/** PRD-Delivery §6.6. A fresh load must present exactly these. */
export const DELIVERY_DEFAULTS: DeliveryTableFilters = {
  source: 'all',
  targeting: 'all',
  specialty: 'all',
  period: '90d',
  recruiterOffering: 'all',
};

/** The chart's defaults — All · All · All · Monthly, as on Regions. */
export const DELIVERY_CHART_DEFAULTS: ChartFilters = CHART_DEFAULTS;

/** Table sort defaults: Active TOA, descending. */
export const DELIVERY_SORT_DEFAULTS = {
  key: 'activeToa',
  direction: 'desc' as const,
};

export function useDeliveryFilters(
  initial: DeliveryTableFilters = DELIVERY_DEFAULTS,
  storageKey?: string,
) {
  const [filters, setFilters] = usePersistentState<DeliveryTableFilters>(
    storageKey,
    initial,
    sanitizeEnumRecord(initial, {
      source: SOURCE_VALUES,
      targeting: TARGETING_VALUES,
      specialty: SPECIALTY_VALUES,
      period: PERIOD_VALUES,
      recruiterOffering: OFFERING_VALUES,
    }),
  );

  return {
    filters,
    set: <K extends keyof DeliveryTableFilters>(key: K, value: DeliveryTableFilters[K]) =>
      setFilters((current) => ({ ...current, [key]: value })),
    reset: () => setFilters(initial),
  };
}
